import React from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  BoxProps,
  Card,
  CardActions,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  MenuItemProps,
  MenuProps,
  Tooltip,
  Typography,
  TypographyProps
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Description as DocumentIcon,
  Link as LinkIcon,
  NearMe as NavigationIcon,
} from '@mui/icons-material';
import { IDocumentLink } from '../../../../dataObjects/models/digitalMedia/DocumentLink';
import { cardGridViewItemStyles } from '../../../styles/views/cardGridViewItemStyles';
import { MenuItemStringAssets, TooltipStringAssets } from '../../../../assets/stringAssets';
import { enumDocumentLinkType } from '../../../../dataObjects/enums';
import { IDocumentLinkViewModel } from '../../../../dataObjects/viewModels/documentLinkViewModel';
import { Channel } from '../../../../dataObjects/models/channels/Channel';


// a styled Popover Menu (used for popping up the 'User' menu)
const StyledPopoverUserMenu = styled((props: MenuProps) => (
  <Menu
    elevation={5}
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  // transform: 'translate(0, 40px)',  // move the court left by 50% of its width
}));

// a styled menu item in the Popover Menu
const StyledPopoverMenuItem = styled((props: MenuItemProps) => (
  <MenuItem
    {...props}
  />
))(({ theme }) => ({
  '&:focus': {
    //       // '&.Mui-focus': {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.light,
  },
  '&:hover': {
    //       // '&.Mui-hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.light,
  },
}));

// styled text for a menu item in the Popover Menu
const StyledMenuItemText = styled((props: TypographyProps) => (
  <Typography
    {...props}
  />
))(({ theme }) => ({
  marginLeft: '1.0rem'
}));

// a styled Box (equivalent to a <div>), representing a background area for controls on the AppHeader
const StyledNavigateToButtonArea = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  justifyItems: 'flex-end',
  marginTop: theme.spacing(-1.50),
}));


// The next line is preserved for update to React 18, once peer dependencies will allow the update
// export interface IDocumentLinksCardGridViewItemProps extends PropsWithChildren<unknown> {
export interface IDocumentLinksCardGridViewItemProps {
  documentLinkViewModel: IDocumentLinkViewModel;
  onDelete: (documentLink: IDocumentLink) => void;
}


export const DocumentLinksCardGridViewItem: React.FC<IDocumentLinksCardGridViewItemProps> = (props: IDocumentLinksCardGridViewItemProps) => {

  const { documentLinkViewModel, onDelete } = props;
  const { documentLink, accessPermissionToAncestorChannel } = documentLinkViewModel;
  const { id, description, documentLinkType, downloadUrl, name } = documentLink;

  const classes = cardGridViewItemStyles();

  // we're going to apply the 'useNavigate' hook to navigate the browser
  const navigate = useNavigate();

  // create an icon and a string based on whether this is a stored document or a linked document
  let storedOrLinkedDocString: string = 'unknown';
  let iconToDisplay: React.JSX.Element = <></>;
  if (documentLinkType === enumDocumentLinkType.GoogleCloudStorage) {
    storedOrLinkedDocString = 'Stored';
    iconToDisplay = <><DocumentIcon className={classes.documentIcon} /></>
  } else if (documentLinkType === enumDocumentLinkType.ExternalSource) {
    storedOrLinkedDocString = 'Linked';
    iconToDisplay = <><LinkIcon className={classes.documentIcon} /></>
  }

  // specifies the anchor position for the "Navigate To" popup menu
  const [navigateToButtonAnchorEl, setUserButtonAnchorEl] = React.useState(null);

  // controls whether the "Navigate To" popup menu is open (showing)
  const openNavigateToMenu = Boolean(navigateToButtonAnchorEl);


  function handleNavigateToButtonClicked(event: any) {
    setUserButtonAnchorEl(event.currentTarget);
  };

  const handleCloseNavigateToMenu = () => {
    setUserButtonAnchorEl(null);
  };

  const handlePageNavigationFromPopupMenu = (pageUrl: string) => {
    // navigate to page
    navigate(pageUrl);

    // close the user menu
    handleCloseNavigateToMenu();
  }

  return (
    <>
      {/* <Card classes={{ root: classes.card }} elevation={6}> */}
      <Card elevation={6} className={classes.card}>
        <CardContent>
          <StyledNavigateToButtonArea>
            <IconButton
              className={`${classes.navigationIconButton}`}
              size="small"
              aria-label="navigate to"
              aria-controls="navigate-to"
              aria-haspopup="true"
              onClick={(evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => handleNavigateToButtonClicked(evt)}
            >
              <Tooltip title={'Navigate to...'} arrow >
                <NavigationIcon fontSize='small' />
              </Tooltip>
            </IconButton>

            {/* Render the Popover User Menu **ONLY** if the navigateToButtonAnchorEl object has been created */}
            {
              navigateToButtonAnchorEl &&
              <StyledPopoverUserMenu
                id="menu-appbar"
                anchorEl={navigateToButtonAnchorEl}
                anchorReference={navigateToButtonAnchorEl && 'anchorEl'}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                open={openNavigateToMenu}
                onClose={handleCloseNavigateToMenu}
              >
                <StyledPopoverMenuItem selected={false} onClick={() => handlePageNavigationFromPopupMenu(`/${Channel.coreChannelIdFromChannelId(documentLinkViewModel.ancestorChannelId)}/topics`)}>
                  <StyledMenuItemText>
                    {MenuItemStringAssets.menuItem_NavigateToAncestorChannel}
                  </StyledMenuItemText>
                </StyledPopoverMenuItem>
                <StyledPopoverMenuItem selected={false} onClick={() => handlePageNavigationFromPopupMenu(`/${documentLinkViewModel.ancestorTopicId}/topicItems`)}>
                  <StyledMenuItemText>
                    {MenuItemStringAssets.menuItem_NavigateToAncestorTopic}
                  </StyledMenuItemText>
                </StyledPopoverMenuItem>
                <StyledPopoverMenuItem selected={false} onClick={() => handlePageNavigationFromPopupMenu(`/${documentLinkViewModel.parentTopicItemId}/digitalMedia`)}>
                  <StyledMenuItemText>
                    {MenuItemStringAssets.menuItem_NavigateToParentTopicItem}
                  </StyledMenuItemText>
                </StyledPopoverMenuItem>
              </StyledPopoverUserMenu>
            } {/* end of 'navigateToButtonAnchorEl && ...' */}

          </StyledNavigateToButtonArea>

          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Tooltip
              // Tooltip for the name of the Document (with prefix to indicate whether it's a stored or linked document)
              title={`(${storedOrLinkedDocString}) ${name}`}
              arrow
            >
              <span>
                {/* DocumentLink: Couldn't get proper behavior from <CardHeader>, so will just display a name via <Typography> */}
                <Typography noWrap variant='body1' align='center' paddingBottom={'1rem'}>
                  {iconToDisplay}
                  {name}
                </Typography>
              </span>
            </Tooltip>
          </a>

          <Typography className={classes.cardDescription} variant="body1">
            {description}
          </Typography>
        </CardContent>

        <CardActions className={classes.cardActionsContainer}>
          <Tooltip
            // the tooltip for the edit button will either be "Edit" (if user has sufficient permission) or "Edit disallowed" (insufficient permission)
            title={!accessPermissionToAncestorChannel.hasEditPermission ? TooltipStringAssets.edit_Disallowed : TooltipStringAssets.edit}
            arrow
          >
            <span>
              <IconButton
                className={`${classes.actionIconButton} ${classes.editIconButton}`}
                size="small"
                aria-label="edit"
                disabled={!accessPermissionToAncestorChannel.hasEditPermission}
                onClick={() => navigate(`/documentLink/${id}`)}>
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            // the tooltip for the delete button will either be "Delete" (if user has sufficient permission) or "Delete disallowed" (insufficient permission)
            title={!accessPermissionToAncestorChannel.hasEditPermission ? TooltipStringAssets.delete_Disallowed : TooltipStringAssets.delete}
            arrow
          >
            <span>
              <IconButton
                className={`${classes.actionIconButton} ${classes.deleteIconButton}`}
                size="small"
                aria-label="delete"
                disabled={!accessPermissionToAncestorChannel.hasEditPermission}
                onClick={() => documentLink && onDelete(documentLink)} >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </CardActions>
      </Card>
    </>
  );

}